/**
 * DelegationList.tsx — List container for delegations.
 * 
 * Handles:
 * - Loading + error states
 * - Empty state messaging
 * - Renders DelegationListItem for each delegation
 */

import React from 'react';
import { DelegationSummary, PendingUpdateSummary } from '../../types/delegation';
import DelegationListItem from './DelegationListItem';
import './DelegationList.scss';

interface DelegationListProps {
  items: DelegationSummary[] | PendingUpdateSummary[];
  loading: boolean;
  error: string | null;
  emptyMessage?: string;
  showDelegator?: boolean; // For "delegated by me" list
}

export const DelegationList: React.FC<DelegationListProps> = ({
  items,
  loading,
  error,
  emptyMessage = 'No delegations found',
  showDelegator = false,
}) => {
  if (loading) {
    return (
      <div className="delegation-list loading">
        <div className="spinner">Loading delegations...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="delegation-list error">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="delegation-list empty">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="delegation-list">
      {items.map((item) => {
        // Handle both DelegationSummary and PendingUpdateSummary
        const delegation = item as any;
        const key = delegation.updateRequestId
          ? `ur-${delegation.updateRequestId}`
          : `d-${delegation.delegationId}`;
        return (
          <DelegationListItem
            key={key}
            delegation={delegation}
            showDelegator={showDelegator}
          />
        );
      })}
    </div>
  );
};

export default DelegationList;
